import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../api/client';
import { useAuth } from '../state/auth';
import { Badge, Bandeau, Carte, Champ, Modale, Squelette, Vide } from '../components/ui';
import { ReleveKm } from '../components/ReleveKm';
import { useAlertes } from '../state/alertes';
import { centimes, dateFr, deviseReference, isoJour, montant } from '../lib/format';
import { useMobile } from '../lib/ecran';
import type { Vehicule } from '../api/types';
import { T, langue, reglagesRegionaux } from '../i18n';

const nombre = (n: number) => n.toLocaleString(langue === 'en' ? 'en-GB' : 'fr-FR');

const VIDE = {
  immatriculation: '', marque: '', modele: '', annee: String(new Date().getFullYear()),
  categorie: 'B — Économique', boite: 'manuelle', carburant: 'essence', km: '0',
  haute: '', basse: '', mois: '', forfaitKmJour: '250', identifiantBoitier: '',
  assurance: '', controle: '', vignette: '',
};


export default function Flotte() {
  const { peut } = useAuth();
  const mobile = useMobile();
  const [v, setV] = useState<Vehicule[] | null>(null);
  const [ouvert, setOuvert] = useState(false);
  const [edite, setEdite] = useState<Vehicule | null>(null);
  const [f, setF] = useState({ ...VIDE });
  const [erreur, setErreur] = useState('');
  const [releve, setReleve] = useState<string | null>(null);
  const [msg, setMsg] = useState('');
  const { recharger: rechargerAlertes } = useAlertes();

  /*
   * LES ARCHIVES SONT UN AUTRE ÉCRAN, RÉSERVÉ AUX GÉRANTS.
   *
   * « Archiver un véhicule : il ne sera plus fonctionnel, et seulement pour
   * Hichem et Reda, dans un nouvel écran. » Une voiture archivée sort du
   * travail courant — planning, disponibilités, échéances — sans que son
   * historique disparaisse. On la retrouve ici, et on peut la remettre en
   * service.
   */
  const [vueArchives, setVueArchives] = useState(false);
  const [archiver, setArchiver] = useState<Vehicule | null>(null);
  const [motifArchive, setMotifArchive] = useState('');
  const charger = useCallback(() => {
    api.get<Vehicule[]>(`/vehicules${vueArchives ? '?archives=1' : ''}`).then(setV);
  }, [vueArchives]);
  useEffect(charger, [charger]);

  async function confirmerArchivage() {
    if (!archiver) return;
    try {
      await api.post(`/vehicules/${archiver._id}/archiver`, { motif: motifArchive });
      setArchiver(null); setMotifArchive(''); setMsg(T.flotte.archivee);
      charger();
    } catch (e) { setMsg(e instanceof ApiError ? e.erreur : T.flotte.archivageEchec); }
  }

  async function remettreEnService(x: Vehicule) {
    try {
      await api.post(`/vehicules/${x._id}/desarchiver`, {});
      setMsg(T.flotte.enService(`${x.marque} ${x.modele}`));
      charger();
    } catch (e) { setMsg(e instanceof ApiError ? e.erreur : T.flotte.remiseEchec); }
  }
  if (!v) return <Squelette lignes={8} />;

  function ouvrirCreation() { setEdite(null); setF({ ...VIDE }); setErreur(''); setOuvert(true); }
  function ouvrirEdition(x: Vehicule) {
    setEdite(x); setErreur('');
    const doc = (t: string) => x.documents.find((d) => d.type === t)?.dateExpiration?.slice(0, 10) ?? '';
    setF({
      immatriculation: x.immatriculation, marque: x.marque, modele: x.modele, annee: String(x.annee),
      categorie: x.categorie, boite: x.boite, carburant: x.carburant, km: String(x.km),
      haute: String(x.tarifHauteSaisonMinor / 100), basse: String(x.tarifBasseSaisonMinor / 100),
      mois: x.tarifMoisMinor ? String(x.tarifMoisMinor / 100) : '',
      forfaitKmJour: String(x.forfaitKmJour), identifiantBoitier: x.identifiantBoitier ?? '',
      assurance: doc('assurance'), controle: doc('controle_technique'), vignette: doc('vignette'),
    });
    setOuvert(true);
  }

  async function enregistrer() {
    setErreur('');
    // Un bouton grisé sans explication laisse l'utilisateur cliquer dans le vide.
    // On laisse cliquer, et on nomme précisément ce qui manque.
    const manquants = [
      !f.immatriculation.trim() && T.flotte.manque.immatriculation,
      !f.marque.trim() && T.flotte.manque.marque,
      !f.modele.trim() && T.flotte.manque.modele,
      !Number(f.haute) && T.flotte.manque.haute,
      !Number(f.basse) && T.flotte.manque.basse,
    ].filter(Boolean) as string[];
    if (manquants.length) {
      setErreur(T.flotte.ilManque(manquants.length, manquants.join(', ')));
      return;
    }
    const documents = [
      f.assurance && { type: 'assurance', dateExpiration: f.assurance },
      f.controle && { type: 'controle_technique', dateExpiration: f.controle },
      f.vignette && { type: 'vignette', dateExpiration: f.vignette },
    ].filter(Boolean);
    const corps = {
      immatriculation: f.immatriculation, marque: f.marque, modele: f.modele, annee: Number(f.annee),
      categorie: f.categorie, boite: f.boite, carburant: f.carburant, km: Number(f.km || 0),
      tarifHauteSaisonMinor: centimes(Number(f.haute || 0)), tarifBasseSaisonMinor: centimes(Number(f.basse || 0)),
      tarifMoisMinor: centimes(Number(f.mois || 0)),
      forfaitKmJour: Number(f.forfaitKmJour || 0), identifiantBoitier: f.identifiantBoitier || undefined,
      documents,
    };
    try {
      if (edite) await api.patch(`/vehicules/${edite._id}`, corps);
      else await api.post('/vehicules', corps);
      setOuvert(false); charger();
    } catch (e) { setErreur(e instanceof ApiError ? e.erreur : T.flotte.erreur); }
  }

  const pastille = (j: number) => j <= 7 ? 'retard' : j <= 30 ? 'retour' : 'en_cours';

  /*
   * L'entretien se lit comme un document : « Vidange · 400 km » a cote de
   * « Assurance · J-12 ». Le patron ne veut pas se demander si l'alerte vient
   * d'un papier ou du garage — c'est la meme question, la meme ligne.
   */
  const badgeEntretien = (e: Vehicule['prochainEntretien']) => {
    if (!e) return null;
    const km = e.resteKm;
    const j = e.resteJours;
    const statut = (km != null && km <= 0) || (j != null && j <= 0) ? 'retard'
      : (km != null && km < 1000) || (j != null && j < 15) ? 'retour' : 'en_cours';
    const texte = km != null
      ? (km <= 0 ? T.flotte.depassee(e.type) : T.flotte.resteKm(e.type, nombre(km), reglagesRegionaux().uniteDistance))
      : T.flotte.resteJours(e.type, j ?? 0);
    return <Badge statut={statut} libelle={texte} />;
  };

  const tf = T.flotte;
  const unite = reglagesRegionaux().uniteDistance;
  const devise = deviseReference();
  return (
    <>
      <div className="entete">
        <div className="titres">
          <h1>{tf.titre}</h1>
          <p>{v.length === 0
            ? tf.sousTitreVide
            : tf.sousTitre(v.length)}</p>
        </div>
        <div className="actions">
          {/* Le kilometrage se releve en passant, pas seulement dans un etat des
              lieux : c'est lui qui declenche la vidange. */}
          {/* Relever un compteur est un AJOUT, pas une modification : Sabrina le
              fait aussi, mais seulement pendant les fenêtres d'inventaire. */}
          {peut('creer.kilometrage') && v.length > 0 && (
            <button className="btn" onClick={() => setReleve('')}>{tf.releverKm}</button>
          )}
          {peut('modifier.vehicule') && (
            <button className="btn" onClick={() => setVueArchives(!vueArchives)}>
              {vueArchives ? tf.retourService : tf.archivees}
            </button>
          )}
          {peut('creer.vehicule') && !vueArchives && (
            <button className="btn btn-p" onClick={ouvrirCreation}>{tf.ajouter}</button>
          )}
        </div>
      </div>

      {/* Sur téléphone, le tableau à neuf colonnes met « Modifier » hors écran :
          on présente une carte par voiture, la carte entière étant l'action. */}
      {msg && <div style={{ marginBottom: 14 }}><Bandeau type="ok">{msg}</Bandeau></div>}

      {v.length === 0 ? (
        <Vide titre={tf.aucune}
              action={peut('creer.vehicule')
                ? <button className="btn btn-p btn-l" onClick={ouvrirCreation}>{tf.ajouterPremiere}</button>
                : undefined}>
          {tf.videTexte}
          {!peut('creer.vehicule') && <>{tf.demandezGerant}</>}
        </Vide>
      ) : mobile ? (
        <div className="cartes">
          {v.map((x) => {
            const alertes = x.alertesDocuments ?? [];
            const pire = alertes.length ? Math.min(...alertes.map((a) => a.joursRestants)) : null;
            return (
              <button key={x._id} className="carte-ligne"
                      onClick={() => (peut('modifier.vehicule') ? ouvrirEdition(x) : undefined)}>
                <div className="cl-tete">
                  <span className="cl-titre">{x.marque} {x.modele}</span>
                  <Badge statut={x.statut} />
                </div>
                <div className="cl-sous">{x.immatriculation} · {x.annee} · {tf.categories[x.categorie] ?? x.categorie}</div>
                {x.prochainEntretien && (
                  <div style={{ marginTop: 6 }}>{badgeEntretien(x.prochainEntretien)}</div>
                )}
                <div className="cl-champs">
                  <div className="cl-champ"><span>{tf.tarifEte}</span><span>{montant(x.tarifHauteSaisonMinor)}</span></div>
                  <div className="cl-champ"><span>{tf.tarifHiver}</span><span>{montant(x.tarifBasseSaisonMinor)}</span></div>
                  <div className="cl-champ"><span>{tf.kilometrage}</span><span>{nombre(x.km)} {unite}</span></div>
                  <div className="cl-champ"><span>{tf.boite}</span><span>{tf.boites[x.boite] ?? x.boite}</span></div>
                </div>
                {alertes.length > 0 && (
                  <div className="cl-pied">
                    {alertes.map((a) => (
                      <Badge key={a.type} statut={pastille(a.joursRestants)}
                             libelle={tf.docJours(tf.typeDoc[a.type] ?? a.type, a.joursRestants)} />
                    ))}
                  </div>
                )}
                {peut('modifier.vehicule') && (
                  <div className="mini" style={{ color: 'var(--primaire)', fontWeight: 600 }}>
                    {tf.toucher}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      ) : (
      <Carte pad={false}>
        <div className="enveloppe-tbl">
          <table className="tbl">
            <thead>
              <tr><th>{tf.colonnes.vehicule}</th><th>{tf.colonnes.immatriculation}</th><th>{tf.colonnes.categorie}</th><th className="droite">{unite === 'km' ? tf.colonnes.km : unite}</th>
                  <th className="droite">{tf.tarifEte}</th><th className="droite">{tf.tarifHiver}</th>
                  <th>{tf.colonnes.documents}</th><th>{tf.colonnes.statut}</th><th /></tr>
            </thead>
            <tbody>
              {v.map((x) => (
                <tr key={x._id}>
                  <td><b>{x.marque} {x.modele}</b> <span className="mini sourdine">{x.annee} · {tf.boites[x.boite] ?? x.boite}</span></td>
                  <td className="num">{x.immatriculation}</td>
                  <td className="mini sourdine">{tf.categories[x.categorie] ?? x.categorie}</td>
                  <td className="droite num">{nombre(x.km)}</td>
                  <td className="droite montant" style={{ background: x.saisonCourante === 'haute' ? 'var(--accent-doux)' : undefined }}>
                    {montant(x.tarifHauteSaisonMinor)}
                  </td>
                  <td className="droite montant" style={{ background: x.saisonCourante === 'basse' ? 'var(--accent-doux)' : undefined }}>
                    {montant(x.tarifBasseSaisonMinor)}
                  </td>
                  <td>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {(x.alertesDocuments ?? []).map((a) => (
                        <Badge key={a.type} statut={pastille(a.joursRestants)}
                               libelle={tf.docJours(tf.typeDoc[a.type] ?? a.type, a.joursRestants)} />
                      ))}
                      {badgeEntretien(x.prochainEntretien)}
                      {(x.alertesDocuments ?? []).length === 0 && !x.prochainEntretien && (
                        <span className="mini sourdine">{tf.aJour}</span>
                      )}
                    </div>
                  </td>
                  <td><Badge statut={x.statut} /></td>
                  <td className="droite">
                    {peut('modifier.vehicule') && (vueArchives
                      ? <button className="btn btn-s" onClick={() => void remettreEnService(x)}>{tf.remettre}</button>
                      : <>
                        <button className="btn btn-s" onClick={() => ouvrirEdition(x)}>{tf.modifier}</button>
                        <button className="btn btn-s" style={{ marginLeft: 6 }} onClick={() => setArchiver(x)}>{tf.archiver}</button>
                      </>)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Carte>
      )}

      <p className="mini sourdine" style={{ marginTop: 12 }}>
        {tf.note}
        <Link to="/disponibilites" style={{ marginLeft: 8 }}>{tf.chercherDispo}</Link>
      </p>

      {releve !== null && (
        <ReleveKm vehicules={v} vehiculeInitial={releve || undefined}
                  onFermer={() => setReleve(null)}
                  onFini={(m) => { setReleve(null); setMsg(m); charger(); rechargerAlertes(); }} />
      )}

      {archiver && (
        <Modale titre={tf.archiverTitre(`${archiver.marque} ${archiver.modele}`)} onFermer={() => setArchiver(null)}>
          <Bandeau type="warn" titre={tf.archiverBandeauTitre}>
            {tf.archiverBandeau}
          </Bandeau>
          <div style={{ marginTop: 14 }}>
            <Champ label={tf.motif} aide={tf.motifAide}>
              <input value={motifArchive} onChange={(e) => setMotifArchive(e.target.value)} />
            </Champ>
          </div>
          <div className="actions" style={{ marginTop: 14, justifyContent: 'flex-end' }}>
            <button className="btn" onClick={() => setArchiver(null)}>{tf.annuler}</button>
            <button className="btn btn-p" disabled={motifArchive.trim().length < 3}
                    onClick={() => void confirmerArchivage()}>{tf.archiver}</button>
          </div>
        </Modale>
      )}

      {ouvert && (
        <Modale titre={edite ? tf.modifierTitre(`${edite.marque} ${edite.modele}`) : tf.ajouter} onFermer={() => setOuvert(false)}>
          <div style={{ display: 'grid', gap: 12, maxHeight: '62vh', overflowY: 'auto', paddingRight: 4 }}>
            <Champ label={tf.immatriculation} aide={tf.immatriculationAide}>
              <input value={f.immatriculation} onChange={(e) => setF({ ...f, immatriculation: e.target.value })} placeholder="045218-116-16" />
            </Champ>
            <div className="grille g2">
              <Champ label={tf.marque}><input value={f.marque} onChange={(e) => setF({ ...f, marque: e.target.value })} /></Champ>
              <Champ label={tf.modele}><input value={f.modele} onChange={(e) => setF({ ...f, modele: e.target.value })} /></Champ>
              <Champ label={tf.annee}><input type="number" value={f.annee} onChange={(e) => setF({ ...f, annee: e.target.value })} /></Champ>
              <Champ label={tf.categorie}>
                <select value={f.categorie} onChange={(e) => setF({ ...f, categorie: e.target.value })}>
                  {['A — Citadine', 'B — Économique', 'C — Compacte', 'D — Berline', 'E — SUV', 'U — Utilitaire'].map((c) => <option key={c} value={c}>{tf.categories[c] ?? c}</option>)}
                </select>
              </Champ>
              <Champ label={tf.boite}>
                <select value={f.boite} onChange={(e) => setF({ ...f, boite: e.target.value })}>
                  <option value="manuelle">{tf.manuelle}</option><option value="automatique">{tf.automatique}</option>
                </select>
              </Champ>
              <Champ label={tf.carburant}>
                <select value={f.carburant} onChange={(e) => setF({ ...f, carburant: e.target.value })}>
                  {['essence', 'diesel', 'gpl', 'hybride'].map((c) => <option key={c} value={c}>{tf.carburants[c] ?? c}</option>)}
                </select>
              </Champ>
            </div>
            <div className="grille g2">
              <Champ label={tf.tarifHaute(devise)} aide={tf.tarifHauteAide}>
                <input type="number" value={f.haute} onChange={(e) => setF({ ...f, haute: e.target.value })} />
              </Champ>
              <Champ label={tf.tarifBasse(devise)} aide={tf.obligatoire}>
                <input type="number" value={f.basse} onChange={(e) => setF({ ...f, basse: e.target.value })} />
              </Champ>
              {/* Le forfait mensuel. Facultatif : une voiture sans ce prix ne se
                  loue tout simplement pas au mois, et le logiciel le dit. */}
              <Champ label={tf.tarifMois(devise)}
                     aide={tf.tarifMoisAide}>
                <input type="number" min={0} step={1000} value={f.mois} placeholder="0"
                       onChange={(e) => setF({ ...f, mois: e.target.value })} />
              </Champ>
              <Champ label={tf.kmActuel}><input type="number" value={f.km} onChange={(e) => setF({ ...f, km: e.target.value })} /></Champ>
              {/*
                * Deux saisies passées sans un mot en octobre : deux voitures à 1 DZD
                * par jour — des locations à 2, 10, 24 DZD sont entrées dans la caisse —
                * et quatre compteurs à 0, qui faussent tout le suivi des vidanges.
                * On ne bloque pas (un tarif spécial existe), mais on le dit en rouge.
                */}
              {[f.haute, f.basse].some((x) => Number(x) > 0 && Number(x) < 1000) && (
                <div style={{ gridColumn: '1 / -1' }}>
                  <Bandeau type="ko">{tf.tarifSuspect(montant(centimes(Math.min(...[f.haute, f.basse].map(Number).filter((x) => x > 0)))))}</Bandeau>
                </div>
              )}
              {!Number(f.km) && (
                <div style={{ gridColumn: '1 / -1' }}><Bandeau type="warn">{tf.kmAZero}</Bandeau></div>
              )}
              <Champ label={tf.forfait(unite)} aide={tf.forfaitAide}>
                <input type="number" value={f.forfaitKmJour} onChange={(e) => setF({ ...f, forfaitKmJour: e.target.value })} />
              </Champ>
            </div>
            <div className="grille g3">
              <Champ label={tf.assuranceExpire}><input type="date" value={f.assurance} onChange={(e) => setF({ ...f, assurance: e.target.value })} /></Champ>
              <Champ label={tf.controle}><input type="date" value={f.controle} onChange={(e) => setF({ ...f, controle: e.target.value })} /></Champ>
              <Champ label={tf.vignette}><input type="date" value={f.vignette} onChange={(e) => setF({ ...f, vignette: e.target.value })} /></Champ>
            </div>
            <Champ label={tf.boitier} aide={tf.boitierAide}>
              <input value={f.identifiantBoitier} onChange={(e) => setF({ ...f, identifiantBoitier: e.target.value })} />
            </Champ>
          </div>
          {erreur && <div style={{ marginTop: 10 }}><Bandeau type="ko">{erreur}</Bandeau></div>}
          <div className="actions" style={{ marginTop: 14, justifyContent: 'flex-end' }}>
            <button className="btn" onClick={() => setOuvert(false)}>{tf.annuler}</button>
            <button className="btn btn-p" onClick={() => void enregistrer()}>
              {edite ? tf.enregistrer : tf.ajouterVoiture}
            </button>
          </div>
        </Modale>
      )}
    </>
  );
}
